import React, { useEffect, useState, useRef } from 'react';
import { HandHeart, Sparkles, Heart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * VerseReactionBar
 *
 * Three anonymous emoji-reactions tied to a verse reference: Praying, Amen, Moved.
 * Counts are live (fetched on mount) and optimistically incremented on tap, with
 * a localStorage guard to prevent obvious spam from the same device.
 *
 * Tiny, fast, ritualistic — it turns passive social proof into one-tap engagement.
 */
const REACTIONS = [
  { id: 'praying', label: 'Praying', Icon: HandHeart, color: 'text-purple-400' },
  { id: 'amen', label: 'Amen', Icon: Sparkles, color: 'text-amber-400' },
  { id: 'moved', label: 'This moved me', Icon: Heart, color: 'text-rose-400' },
];

const lsKey = (ref, id) => `lumera.react.${ref}.${id}`;

const VerseReactionBar = ({ verse, variant = 'dark' }) => {
  const [counts, setCounts] = useState({ praying: 0, amen: 0, moved: 0 });
  const [pending, setPending] = useState(null);
  const [tapped, setTapped] = useState(() => ({}));
  const ref = verse?.reference;
  const prevRef = useRef(null);

  // Load counts on mount / ref change
  useEffect(() => {
    if (!ref || prevRef.current === ref) return;
    prevRef.current = ref;
    (async () => {
      try {
        const res = await fetch(`${API}/api/shares/reactions/${encodeURIComponent(ref)}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data?.counts) setCounts(data.counts);
      } catch (_) {
        /* silent */
      }
    })();
    // Restore tapped state from localStorage
    const nextTapped = {};
    REACTIONS.forEach((r) => {
      try {
        if (localStorage.getItem(lsKey(ref, r.id))) nextTapped[r.id] = true;
      } catch (_) {
        /* ignore */
      }
    });
    setTapped(nextTapped);
  }, [ref]);

  const sendReaction = async (id) => {
    if (!ref || pending === id || tapped[id]) return;
    setPending(id);
    // Optimistic increment
    setCounts((c) => ({ ...c, [id]: (c[id] || 0) + 1 }));
    setTapped((t) => ({ ...t, [id]: true }));
    try {
      localStorage.setItem(lsKey(ref, id), '1');
    } catch (_) {
      /* ignore */
    }
    try {
      await fetch(`${API}/api/shares/react`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verse_reference: ref, reaction: id }),
      });
    } catch (_) {
      /* non-critical */
    } finally {
      setPending(null);
    }
  };

  if (!verse) return null;
  const isDark = variant === 'dark';

  return (
    <div
      className="flex flex-wrap items-center justify-center gap-2 sm:gap-3"
      data-testid="verse-reaction-bar"
    >
      {REACTIONS.map(({ id, label, Icon, color }) => {
        const isTapped = !!tapped[id];
        const count = counts[id] || 0;
        return (
          <button
            key={id}
            onClick={() => sendReaction(id)}
            disabled={isTapped || pending === id}
            data-testid={`reaction-btn-${id}`}
            className={`group inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
              isDark
                ? `border ${
                    isTapped
                      ? 'bg-white/15 border-white/30 text-white'
                      : 'bg-white/5 border-white/15 text-slate-200 hover:bg-white/10 hover:border-white/25'
                  }`
                : `border ${
                    isTapped
                      ? 'bg-slate-900/10 border-slate-300 text-slate-900'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`
            } ${pending === id ? 'opacity-80 scale-95' : 'hover:scale-[1.03]'} disabled:cursor-default`}
          >
            <Icon
              className={`w-4 h-4 ${color} ${
                pending === id ? 'animate-pulse' : isTapped ? '' : 'group-hover:scale-110 transition-transform'
              }`}
            />
            <span>{label}</span>
            <span
              className={`tabular-nums text-xs ${
                isDark ? 'text-slate-300' : 'text-slate-500'
              }`}
              data-testid={`reaction-count-${id}`}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default VerseReactionBar;
